import "./BulkTagByDateRangeModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import DatePicker from "components/inputs/DatePicker/DatePicker";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useMemo, useRef, useState } from "react";
import {
  DOCUMENT_GROUP_TAG_LABELS,
  DOCUMENT_GROUP_TAG_TYPES,
  useBulkAssignDocumentGroupTagsByDateRangeMutation,
} from "services/document-group-tag-api";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";

import * as Sentry from "@sentry/react";

import { MEDICAL_RECORDS_DOCUMENT_TYPE } from "../../MetadocConstants";

import type ReactDatePicker from "react-datepicker";
import type { GroupingSource } from "services/metadoc-api";
import type { TocEntry } from "../../metadoc.types";

const TAG_TYPE = DOCUMENT_GROUP_TAG_TYPES.CMS_SUBMISSION;
const TAG_LABEL = DOCUMENT_GROUP_TAG_LABELS[TAG_TYPE];
const ISO_DATE_FORMAT = "yyyy-MM-dd";

const toIsoDate = (date: Date | null) => DATE_TO_STRING(date, ISO_DATE_FORMAT);

const pluralizeRecords = (count: number) =>
  `${count} Medical Record${count === 1 ? "" : "s"}`;

export type DateRangeTagPreview = {
  toTagCount: number;
  alreadyTaggedCount: number;
  undatedCount: number;
};

export const getDateRangeTagPreview = (
  entries: TocEntry[],
  startDate: Date | null,
  endDate: Date | null,
): DateRangeTagPreview => {
  const medicalRecords = entries.filter(
    (entry) =>
      typeof entry.documentGroupId === "number" &&
      !entry.isPendingDelete &&
      entry.category === MEDICAL_RECORDS_DOCUMENT_TYPE,
  );
  const datedRecords = medicalRecords
    .map((entry) => ({
      entry,
      date: toIsoDate(STRING_TO_DATE(entry.dateOfService ?? null)),
    }))
    .filter((record) => record.date !== null);
  const undatedCount = medicalRecords.length - datedRecords.length;
  const start = toIsoDate(startDate);
  const end = toIsoDate(endDate);

  if (!start || !end || start > end) {
    return { toTagCount: 0, alreadyTaggedCount: 0, undatedCount };
  }

  const inRange = datedRecords.filter(
    ({ date }) => date !== null && date >= start && date <= end,
  );
  const alreadyTaggedCount = inRange.filter(({ entry }) =>
    entry.documentGroupTags?.some((tag) => tag.type === TAG_TYPE),
  ).length;

  return {
    toTagCount: inRange.length - alreadyTaggedCount,
    alreadyTaggedCount,
    undatedCount,
  };
};

type BulkTagByDateRangeModalProps = {
  onClose: () => void;
  metadocId: number;
  groupingSource: GroupingSource;
  entries: TocEntry[];
};

const BulkTagByDateRangeModal = ({
  onClose,
  metadocId,
  groupingSource,
  entries,
}: BulkTagByDateRangeModalProps) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const startPickerRef = useRef<ReactDatePicker>(null);
  const endPickerRef = useRef<ReactDatePicker>(null);
  const [bulkAssign, { isLoading }] =
    useBulkAssignDocumentGroupTagsByDateRangeMutation();

  const preview = useMemo(
    () => getDateRangeTagPreview(entries, startDate, endDate),
    [entries, startDate, endDate],
  );
  const hasDateRange = startDate !== null && endDate !== null;
  const isRangeInvalid =
    hasDateRange && (toIsoDate(startDate) ?? "") > (toIsoDate(endDate) ?? "");
  const canConfirm =
    hasDateRange && !isRangeInvalid && preview.toTagCount > 0 && !isLoading;

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    if (date) {
      startPickerRef.current?.setOpen(false, true);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    if (date) {
      endPickerRef.current?.setOpen(false, true);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    const start_date = toIsoDate(startDate);
    const end_date = toIsoDate(endDate);
    if (!start_date || !end_date) {
      return;
    }

    try {
      const result = await bulkAssign({
        metadoc: metadocId,
        source: groupingSource,
        type: TAG_TYPE,
        start_date,
        end_date,
      }).unwrap();

      KaboodleToaster.success(
        `${TAG_LABEL} assigned to ${pluralizeRecords(result.created.length)}`,
      );
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: "metadoc",
          action: "bulk_assign_document_group_tag_by_date_range",
        },
        extra: { metadocId, groupingSource, start_date, end_date },
      });
      KaboodleToaster.error(`Failed to assign ${TAG_LABEL}. Please try again.`);
    }
  };

  return (
    <KaboodleModal
      isOpen
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={() => void handleConfirm()} disabled={!canConfirm}>
            {isLoading ? "Tagging..." : `Tag ${TAG_LABEL}`}
          </Button>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading={`Tag Medical Records as ${TAG_LABEL}`}>
        <p>
          Every Medical Record dated within this range will be tagged as{" "}
          {TAG_LABEL}.
        </p>
        <fieldset className="bulk-tag-by-date-range__dates">
          <DatePicker
            ref={startPickerRef}
            label="From"
            name="bulk-tag-start-date"
            onDateChange={handleStartDateChange}
            allowFutureDates={true}
          />
          <DatePicker
            ref={endPickerRef}
            label="To"
            name="bulk-tag-end-date"
            onDateChange={handleEndDateChange}
            allowFutureDates={true}
          />
        </fieldset>
        {isRangeInvalid && (
          <p className="bulk-tag-by-date-range__error">
            End date must be on or after start date.
          </p>
        )}
        {hasDateRange && !isRangeInvalid && (
          <ul
            className="bulk-tag-by-date-range__preview"
            aria-label="Tagging preview"
          >
            <li>{pluralizeRecords(preview.toTagCount)} will be tagged</li>
            {preview.alreadyTaggedCount > 0 && (
              <li>
                {pluralizeRecords(preview.alreadyTaggedCount)} already tagged
              </li>
            )}
          </ul>
        )}
        {preview.undatedCount > 0 && (
          <p>
            {pluralizeRecords(preview.undatedCount)} with no date will be
            skipped.
          </p>
        )}
      </Card>
    </KaboodleModal>
  );
};

export default BulkTagByDateRangeModal;
